import { Autocomplete, Button, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { AutoAwesome, Close } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { Col, Modal, Row } from 'antd';
import { useAlertLoading } from 'components/UI/alertSakeBar/alertProvider';
import { get, filter, find } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleAutoSelectStandards } from 'store/story/thunks';
import styled from 'styled-components';
import { isQuestionEmpty, validateMatchMaking } from 'features/forms/assignment/studentMatchMaking/helper';
import { useStandards } from 'store/story/selectors';
import { getStateNameFromCode, statesMatch, isIllinoisOrCommonCoreState, getStateNameDisplayForCode } from 'utils/lib/standardHelper';

const ModalWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
`;

const ModalContent = styled.div`
   position: relative;
   max-width: 100vw;
   min-width: 500px;
   padding: 32px;
   border-radius: 12px;
   background-color: #fff;
   box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
   width: 100%;
   max-width: 650px;
   @media (max-width: 1366px) {
     width: 90vw;
   }
 
   @media (max-width: 500px) {
     min-width: 90vw;
     padding: 24px;
     max-height: 560px;
  }
`;

const ModalConfigArea = styled.div`
  max-height: 70vh;
  overflow: auto;
  margin-top: 16px;
  overflow-y: scroll;
  overflow-x: hidden;
  padding-right: 8px;
  
  ::-webkit-scrollbar {
    width: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const LinkArea = styled.div`
  padding: 24px;
  margin: 0;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  margin-top: 16px;
  margin-bottom: 24px;
  background-color: #fafafa;
`;

const TitleText = styled.span`
    font-size: 20px;
    color: #1a1a1a;
    font-weight: 600;
    letter-spacing: 0.3px;
`;

const FormRow = styled(Row)`
  display: flex;
  justify-content: center;
  margin-bottom: 0 !important;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
`;

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 120,
    width: '100%',
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
  },
}));

const AutoSelectStandardsModalContent = ({ 
  onClose, 
  booksAllList, 
  bookId, 
  pageId,
  questions = [],
  setQuestions,
  setButtonDisabled,
  pages: currentBookPages = []
}) => {
  const [plan, setPlan] = useState({
    resourceId: '',
    from: '',
    to: ''
  });
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedLinkBook, setSelectedLinkBook] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const classes = useStyles();
  const dispatch = useDispatch();
  const {setAlertLoading, showAlert} = useAlertLoading();
  const standards = useStandards();

  const bookData = useMemo(() => {
    return find(booksAllList, (bk) => bk.id == bookId);
  }, [booksAllList, bookId]);

  const gradeLevels = useMemo(() => {
    return bookData?.grade_levels || [];
  }, [bookData]);

  const selectedBookData = useMemo(() => {
    return find(booksAllList, (bk) => bk.id == plan?.resourceId);
  }, [booksAllList, plan?.resourceId]);

  const selectedBookGradeLevels = useMemo(() => {
    return selectedBookData?.grade_levels || [];
  }, [selectedBookData]);

  const hasMultipleStates = useMemo(() => {
    return selectedBookGradeLevels.length > 1;
  }, [selectedBookGradeLevels]);

  const normalizeGrade = (grade, stateCode = null) => {
    const gradeStr = String(grade).trim();
    let normalized = gradeStr.replace(/\//g, '-');
    
    if (stateCode && isIllinoisOrCommonCoreState(stateCode)) {
      const gradeNumbers = normalized.split(/[,\-\/]/).map(g => parseInt(g.trim())).filter(g => !isNaN(g));
      
      if (gradeNumbers.length === 1) {
        const singleGrade = gradeNumbers[0];
        if (singleGrade === 9 || singleGrade === 10) {
          return '9-10';
        }
        if (singleGrade === 11 || singleGrade === 12) {
          return '11-12';
        }
      }
      
      if (gradeNumbers.length === 2) {
        const grade1 = gradeNumbers[0];
        const grade2 = gradeNumbers[1];
        
        if ((grade1 === 9 && grade2 === 10) || (grade1 === 10 && grade2 === 9)) {
          return '9-10';
        }
        if ((grade1 === 11 && grade2 === 12) || (grade1 === 12 && grade2 === 11)) {
          return '11-12';
        }
      }
    }
    
    return normalized;
  };

  const availableGradesForState = useMemo(() => {
    if (!selectedState || !standards || !Array.isArray(standards)) return [];
    
    const stateStandards = standards.filter((st) => {
      const standardState = get(st, 'state', '');
      return statesMatch(standardState, selectedState);
    });
    
    const gradeSet = new Set();
    stateStandards.forEach((st) => {
      const gradeLevel = get(st, 'grade_level', '');
      if (gradeLevel) {
        const grades = String(gradeLevel).split(',').map(g => g.trim()).filter(g => g);
        grades.forEach(g => {
          const normalized = normalizeGrade(g, selectedState);
          gradeSet.add(normalized);
        });
      }
    });
    
    const selectedGradeLevel = selectedBookGradeLevels.find(gl => gl.state === selectedState);
    const bookGradeLevel = selectedGradeLevel?.grade_level || '';
    
    if (bookGradeLevel) {
      const bookGrades = String(bookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
      const matchingGrades = [];
      bookGrades.forEach(bg => {
        const normalized = normalizeGrade(bg, selectedState);
        if (gradeSet.has(normalized)) {
          matchingGrades.push(normalized);
        }
      });
      return Array.from(new Set(matchingGrades)).sort();
    }
    
    return Array.from(gradeSet).sort();
  }, [selectedState, standards, selectedBookGradeLevels]);

  useEffect(() => {
    if (selectedState && availableGradesForState.length > 0) {
      const selectedGradeLevel = selectedBookGradeLevels.find(gl => gl.state === selectedState);
      const bookGradeLevel = selectedGradeLevel?.grade_level || '';
      
      if (bookGradeLevel) {
        const bookGrades = String(bookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
        const normalizedBookGrades = bookGrades.map(bg => normalizeGrade(bg, selectedState));
        const matchingGrade = availableGradesForState.find(ag => 
          normalizedBookGrades.includes(ag)
        );
        if (matchingGrade) {
          setSelectedGrade(matchingGrade);
        } else if (availableGradesForState.length > 0) {
          setSelectedGrade(availableGradesForState[0]);
        } else {
          setSelectedGrade('');
        }
      } else if (availableGradesForState.length > 0) {
        setSelectedGrade(availableGradesForState[0]);
      } else {
        setSelectedGrade('');
      }
    } else {
      setSelectedGrade('');
    }
  }, [selectedState, availableGradesForState, selectedBookGradeLevels]);
  

  useEffect(() => {
    const linkBookPages = booksAllList.find((bk) => bk.id == bookId)?.pages;
    setSelectedLinkBook(linkBookPages || []);
    
    const initialFrom = pageId ? parseInt(pageId) : '';
    
    setPlan({
      resourceId: bookId || '',
      from: initialFrom,
      to: ''
    });
    
    const currentBookGradeLevels = selectedBookData?.grade_levels || [];
    if (currentBookGradeLevels.length > 0) {
      setSelectedState(currentBookGradeLevels[0].state || '');
    } else {
      setSelectedState('');
    }
    setSelectedGrade('');
    
    setError(false);
    setErrorMsg("");
  }, [bookId, booksAllList, pageId, selectedBookData]);

  const dropdownPages = useMemo(() => {
    if (selectedLinkBook && Array.isArray(selectedLinkBook) && selectedLinkBook.length > 0) {
      return selectedLinkBook.map((page) => ({
        ...page,
        NumberMasked: page.NumberMasked || `Page ${page.pageId}`,
        pageId: parseInt(page.pageId)
      }));
    }
    if (!plan?.resourceId) {
      if (currentBookPages && Array.isArray(currentBookPages) && currentBookPages.length > 0) {
        return currentBookPages.map((page) => ({
          ...page,
          NumberMasked: page.NumberMasked || `Page ${page.pageId}`,
          pageId: parseInt(page.pageId)
        }));
      }
      return [];
    }
    const selectedBook = booksAllList.find((book) => book.id == plan.resourceId);
    if (selectedBook && selectedBook.pages && Array.isArray(selectedBook.pages)) {
      return selectedBook.pages.map((page) => ({
        ...page,
        NumberMasked: page.NumberMasked || `Page ${page.pageId}`,
        pageId: parseInt(page.pageId)
      }));
    }
    return [];
  }, [plan?.resourceId, booksAllList, selectedLinkBook, currentBookPages]);
  
  // Validation function similar to AssignmentForm
  const validateQuestions = () => {
    const validationErrors = [];
    
    if (!Array.isArray(questions) || questions.length === 0) {
      return { isValid: false, error: "Questions are required, please add at least one question." };
    }

    questions.forEach((q, i) => {
      // Validate question text for most types
      if (q.type === 'email' || q.type === 'essay' || q.type === 'multipleChoice' || 
          q.type === 'trueFalse' || q.type === "multipleResponse" || 
          q.type === "audioMultipleChoice" || q.type === 'audioTrueFalse' || 
          q.type === 'audioEssay') {
        if (isQuestionEmpty(q.question)) {
          validationErrors.push(`Question ${i + 1}: Question text is required.`);
        }
      }

      // Validate instruction for audioToImage
      if (q.type === "audioToImage") {
        if (isQuestionEmpty(q.question)) {
          validationErrors.push(`Question ${i + 1}: Instruction text is required.`);
        }
      }

      // Validate options for multiple choice types
      if (q.type === 'multipleChoice' || q.type === 'trueFalse' || 
          q.type === 'audioTrueFalse' || q.type === 'audioMultipleChoice' || 
          q.type === 'multipleResponse') {
        const noOption = filter(q.options, (o) => !o);
        if (noOption.length > 0) {
          validationErrors.push(`Question ${i + 1}: Option text is required.`);
        }
      }

      // Validate essay expected answers
      if (q.type === 'essay') {
        if (Array.isArray(q.expected_answer) && q.expected_answer.length > 0) {
          const emptyResponseIndices = [];
          q.expected_answer.forEach((ans, ansIndex) => {
            if (isQuestionEmpty(ans)) {
              emptyResponseIndices.push(ansIndex + 1);
            }
          });
          if (emptyResponseIndices.length > 0) {
            validationErrors.push(`Question ${i + 1}: Expected Response no (${emptyResponseIndices.join(',')}) is required.`);
          }
        }
      }

      // Validate picture type
      if (q.type === "picture") {
        if ((!q.pictures || q.pictures.length === 0 || q.pictures === "") && isQuestionEmpty(q.question)) {
          validationErrors.push(`Question ${i + 1}: At least one thing is required either question or picture.`);
        }
      }

      // Validate match type
      if (q?.type === 'match') {
        const matchErrors = validateMatchMaking(q, i);
        matchErrors.forEach(err => {
          validationErrors.push(err.message);
        });
      }

      // Validate photoCollage
      if (q.type === "photoCollage") {
        if (q?.categoryType?.includes("Text") || q?.categoryType?.includes("Audio")) {
          if (q?.type_question_desc?.length === 0 || !q?.type_question_desc) {
            validationErrors.push(`Question ${i + 1}: Collage Description is required.`);
          }
        }
      }

      // Validate filling types
      if (q.type === "filling" || q.type === "fillingImage") {
        if (q.type === "fillingImage" || q.fillingType === 'Audio' || q.fillingType === 'Image') {
          q.filling_image?.forEach((element, index) => {
            if (!element?.url || element?.url?.length === 0) {
              validationErrors.push(`Question ${i + 1}.${index + 1}: ${q.fillingType} is required.`);
            }
          });
        }
        if (isQuestionEmpty(q.question)) {
          validationErrors.push(`Question ${i + 1}: Instruction is required.`);
        }
      }
    });

    if (validationErrors.length > 0) {
      return { 
        isValid: false, 
        error: validationErrors.join(' ') 
      };
    }

    return { isValid: true, error: null };
  };

  const handleAutoSelect = async () => {
    // Validate questions first
    const validation = validateQuestions();
    if (!validation.isValid) {
      showAlert(validation.error, "error");
      return;
    }

    const pageLengthById = booksAllList?.find((book) => book.id === plan?.resourceId)?.pages?.length;

    if (!(plan.from >= 0 && plan.resourceId && plan.to >= 0)) {
      setError(true);
      setErrorMsg("All fields are required.");
      return;
    } else if(parseInt(plan.from) >= parseInt(plan.to) || parseInt(plan.from) == 0) {
      setError(true);
      setErrorMsg(plan.from == 0 ? "From pageId must be greater than zero" : "From pageId must be less than To pageId");
      return;
    } else if (parseInt(plan.to) > pageLengthById) {
      setError(true);
      setErrorMsg(`The selected book has only ${pageLengthById} pages, and you must select within the available range.`);
      return;
    }
    
    try {
      if (setButtonDisabled) setButtonDisabled(true);
      setAlertLoading(true, 'Analyzing questions and selecting standards...');

      const stateToUse = hasMultipleStates ? selectedState : (selectedBookGradeLevels[0]?.state || selectedState || '');
      const state = stateToUse || null;
      const grade = selectedGrade || null;
      
      if (!state || !grade) {
        setError(true);
        setErrorMsg("State and grade level are required.");
        if (setButtonDisabled) setButtonDisabled(false);
        setAlertLoading(false, '');
        return;
      }
      
      // Prepare questions payload in the required format
      const questionsPayload = questions.map((q, index) => ({
        type: q.type,
        id: q.id || index + 1,
        expected_answer: q.expected_answer || [],
        question: q.question || ""
      }));

      const result = await dispatch(handleAutoSelectStandards({
        questions: questionsPayload,
        bookId: String(plan.resourceId),
        fromPage: parseInt(plan.from),
        toPage: plan.to ? parseInt(plan.to) : null,
        grade: grade,
        state: state
      }));
      
      if (result && Array.isArray(result) && result.length > 0) {
        const updatedQuestions = [...questions];
        
        result.forEach((item) => {
          const questionIndex = item.questionIndex;
          if (updatedQuestions[questionIndex]) {
            if (item.state_standard && Array.isArray(item.state_standard)) {
              updatedQuestions[questionIndex].state_standard = item.state_standard;
            } else if (item.standards && Array.isArray(item.standards) && item.standards.length > 0) {
              const validStandards = item.standards.filter((abbr) => {
                return find(standards, (st) => st.abbr === abbr) !== undefined;
              });
              
              if (validStandards.length > 0) {
                updatedQuestions[questionIndex].state_standard = [{
                  grade: parseInt(grade),
                  standard: validStandards,
                  standard_name: state
                }];
              }
            } else if (item.standardId !== undefined && item.standardId !== null) {
              const standard = find(standards, (st) => String(st.id) === String(item.standardId));
              if (standard && standard.abbr) {
                updatedQuestions[questionIndex].state_standard = [{
                  grade: parseInt(grade),
                  standard: [standard.abbr],
                  standard_name: state
                }];
              }
            }
          }
        });
        
        if (setQuestions) {
          setQuestions(updatedQuestions);
        }
        
        showAlert("Standards have been auto-selected for all questions", "success");
        onClose();
      } else {
        showAlert("No standards were matched. Please select standards manually.", "warning");
      }
    } catch (error) {
      console.error('Error auto-selecting standards:', error);
      showAlert("Failed to auto-select standards. Please try again.", "error");
    } finally {
      if (setButtonDisabled) setButtonDisabled(false);
      setAlertLoading(false, '');
    }
  };

  const disableBtn = () => {
    if (!Boolean(plan) || !(plan.from >= 0 && plan.resourceId && plan.to >= 0)) {
      return true;
    }
    if (hasMultipleStates && !selectedState) {
      return true;
    }
    if (!selectedGrade) {
      return true;
    }
    return false;
  };

  const renderLabel = (value) => {
    const stateName = getStateNameDisplayForCode(value);
    return stateName || value;
  };

  return (
    <ModalContent>
      <Row style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #e8e8e8', width: '100%' }}>
        <TitleText>Auto Select Standards</TitleText>
        <IconButton
          onClick={onClose}
          style={{ padding: 4 }}
          size="small"
        >
          <Close />
        </IconButton>
      </Row>
      <ModalConfigArea>
        <LinkArea>
          <FormRow gutter={[0, 24]} style={{ marginTop: 0 }}>
            <Col span={24}>
              <Autocomplete
                id="bookId"
                options={booksAllList}
                getOptionLabel={(option) => `${option.description || ""} (${option.id || ""})`}
                value={booksAllList.find((bk) => bk.id === get(plan, "resourceId")) || null}
                onChange={(event, newValue) => {
                  setError(false);
                  setPlan({
                    ...plan,
                    resourceId: newValue ? newValue.id : "",
                    from: '',
                    to: '',
                  });
                  setSelectedLinkBook(newValue?.pages || []);
                  const newBookGradeLevels = newValue?.grade_levels || [];
                  if (newBookGradeLevels.length > 0) {
                    setSelectedState(newBookGradeLevels[0].state || '');
                  } else {
                    setSelectedState('');
                  }
                  setSelectedGrade('');
                }}
                className={classes.autocomplete}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Book" 
                    variant="outlined"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '0px',
                        borderRadius: '8px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 14px'
                      }
                    }}
                  />
                )}
              />
            </Col>
          </FormRow>
          <FormRow gutter={[0, 24]} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Autocomplete
                id="fromPage"
                options={dropdownPages || []}
                getOptionLabel={(option) => {
                  if (!option || typeof option !== 'object') return '';
                  const pageId = option.pageId != null ? option.pageId : '';
                  return String(option.NumberMasked || `Page ${pageId}` || '');
                }}
                value={dropdownPages?.find(page => page.pageId == parseInt(plan?.from)) || null}
                onChange={(event, newValue) => {
                  setError(false);
                  setPlan({
                    ...plan,
                    from: newValue ? newValue.pageId : '',
                    to: '',
                  });
                }}
                className={classes.autocomplete}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="From Page" 
                    variant="outlined"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '0px',
                        borderRadius: '8px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 14px'
                      }
                    }}
                  />
                )}
              />
            </Col>
          </FormRow>
          <FormRow gutter={[0, 24]} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Autocomplete
                id="toPage"
                options={(() => {
                  if (!plan?.from || !dropdownPages) return [];
                  
                  const fromPageId = parseInt(plan.from);
                  
                  return dropdownPages.filter(page => {
                    const pageId = parseInt(page.pageId);
                    return pageId >= fromPageId;
                  }).sort((a, b) => parseInt(a.pageId) - parseInt(b.pageId));
                })()}
                getOptionLabel={(option) => {
                  if (!option || typeof option !== 'object') return '';
                  const pageId = option.pageId != null ? option.pageId : '';
                  return String(option.NumberMasked || `Page ${pageId}` || '');
                }}
                value={(() => {
                  if (!plan?.to || !dropdownPages) return null;
                  return dropdownPages.find(page => page.pageId == parseInt(plan?.to)) || null;
                })()}
                onChange={(event, newValue) => {
                  setError(false);
                  setPlan({
                    ...plan,
                    to: newValue ? newValue.pageId : '',
                  });
                }}
                disabled={!plan?.from}
                className={classes.autocomplete}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="To Page" 
                    variant="outlined"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '0px',
                        borderRadius: '8px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '14px 14px'
                      }
                    }}
                    placeholder={!plan?.from ? "Please select 'From Page' first" : "Select ending page"}
                  />
                )}
              />
            </Col>
          </FormRow>
          {/* {hasMultipleStates && ( */}
            <FormRow gutter={[0, 24]} style={{ marginTop: 24 }}>
              <Col span={24}>
                <FormControl variant="outlined" className={classes.formControl} fullWidth>
                  <InputLabel id="state-select-label" style={{ backgroundColor: "#fff", paddingLeft: 2, paddingRight: 2 }}>
                    State
                  </InputLabel>
                  <Select
                    labelId="state-select-label"
                    id="state-select"
                    value={selectedState}
                    onChange={(e) => {
                      setError(false);
                      setSelectedState(e.target.value);
                    }}
                    label="State"
                  >
                    {selectedBookGradeLevels.map((gl) => (
                      <MenuItem key={gl.state} value={gl.state}>
                        {renderLabel(gl.state)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Col>
            </FormRow>
            <FormRow gutter={[0, 24]} style={{ marginTop: 24 }}>
              <Col span={24}>
                <FormControl variant="outlined" className={classes.formControl} fullWidth>
                  <InputLabel id="grade-select-label" style={{ backgroundColor: "#fff", paddingLeft: 2, paddingRight: 2 }}>
                    Grade
                  </InputLabel>
                  <Select
                    labelId="grade-select-label"
                    id="grade-select"
                    value={selectedGrade}
                    onChange={(e) => {
                      setError(false);
                      setSelectedGrade(e.target.value);
                    }}
                    label="Grade"
                    disabled={!selectedState || availableGradesForState.length === 0}
                  >
                    {availableGradesForState.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Col>
            </FormRow>
          {/* )} */}
        </LinkArea>
        {error ? (
          <ErrorContainer>
            <FormHelperText error style={{ textAlign: 'center', fontSize: '14px' }}>
              {errorMsg}
            </FormHelperText>
          </ErrorContainer>
        ) : null}
        <ActionButtonContainer>
          <Button
            fullWidth
            disabled={disableBtn() || questions.length === 0}
            onClick={handleAutoSelect}
            variant="contained"
            size="large"
            style={{ 
              maxWidth: '400px',
              height: '48px',
              fontSize: '16px',
              fontWeight: 600,
              borderRadius: '8px',
              textTransform: 'none',
              background: disableBtn() || questions.length === 0 
                ? undefined 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              boxShadow: disableBtn() || questions.length === 0 
                ? undefined 
                : '0 4px 15px rgba(102, 126, 234, 0.4)',
            }}
            sx={{
              '&:hover': {
                background: disableBtn() || questions.length === 0 
                  ? undefined 
                  : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: disableBtn() || questions.length === 0 
                  ? undefined 
                  : '0 6px 20px rgba(102, 126, 234, 0.5)',
              },
              '&:disabled': {
                background: undefined,
              }
            }}
            >
            <AutoAwesome/> &nbsp; Auto Select Standards
          </Button>
        </ActionButtonContainer>
      </ModalConfigArea>
    </ModalContent>
  );
};

const AutoSelectStandardsModal = ({ 
  booksAllList, 
  bookId, 
  pageId,
  questions = [],
  setQuestions,
  buttonDisabled,
  setButtonDisabled,
  pages: currentBookPages = []
}) => {
  const [open, setOpen] = useState(false);

  const bookData = useMemo(() => {
    return find(booksAllList, (bk) => bk.id == bookId);
  }, [booksAllList, bookId]);

  const gradeLevels = useMemo(() => {
    return bookData?.grade_levels || [];
  }, [bookData]);

  const shouldShowButton = useMemo(() => {
    return gradeLevels.length > 0;
  }, [gradeLevels]);

  const handleClose = () => {
    setOpen(false);
  };

  if (!shouldShowButton) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={() => setOpen(true)}
        disabled={buttonDisabled || questions.length === 0}
        style={{
          borderRadius: '6px',
          textTransform: 'none',
          fontWeight: 500,
          background: buttonDisabled || questions.length === 0 
            ? undefined 
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          boxShadow: buttonDisabled || questions.length === 0 
            ? undefined 
            : '0 4px 15px rgba(102, 126, 234, 0.4)',
        }}
        sx={{
          '&:hover': {
            background: buttonDisabled || questions.length === 0 
              ? undefined 
              : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            boxShadow: buttonDisabled || questions.length === 0 
              ? undefined 
              : '0 6px 20px rgba(102, 126, 234, 0.5)',
          },
          '&:disabled': {
            background: undefined,
          }
        }}
      >
        <AutoAwesome /> &nbsp; Auto Select Standards
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        style={{ height: "100%", width: "100%" }}
        footer={null}
        closable={false}
      >
        <ModalWrapper>
          <AutoSelectStandardsModalContent
            onClose={handleClose}
            booksAllList={booksAllList}
            bookId={bookId}
            pageId={pageId}
            questions={questions}
            setQuestions={setQuestions}
            setButtonDisabled={setButtonDisabled}
            pages={currentBookPages}
          />
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default AutoSelectStandardsModal;
